/**
 * Колонки «за период» таблицы новой версии — показатели за любой отрезок
 * дат, который задаёт календарь блока (lib/period-metrics.ts считает,
 * app/api/period-metrics отдаёт). Ключ колонки: `period:<источник>:<показатель>`.
 *
 * Источники и их история (год, см. lib/daily-coverage.ts):
 *   orders    — заказы по дням (product_order_daily);
 *   money     — деньги по товару из начислений (те же показатели, что у
 *               колонок money_* за 7/14/30 дней — lib/money-column-metrics.ts);
 *   analytics — аналитика Ozon по дням (показы, корзины, сессии);
 *   ads       — реклама по товарам и дням (Performance);
 *   stock     — остатки по дням (product_stock_daily): на конец отрезка,
 *               средний, дней в нуле, в пути на конец;
 *   returns   — возвраты и невыкупы по дню начала возврата
 *               (customer_return_history);
 *   supplies  — заявки на поставку по дню создания (supply_order_history);
 *   price     — цена в кабинете по дням (price_history, журнал изменений
 *               «тянется» через дни без записи): средняя, на конец, смен.
 *
 * Модуль без базы: его читают и клиент (подписи, формат, значения ячеек), и
 * сервер (сборка ответа). Правило реестра колонок: новый показатель за
 * период добавляется сюда — подпись, формат, категория редактора и ячейка
 * появляются сами.
 */
import type { MoneyTypeInfo, ProductMoney } from "@/lib/product-money-types";
import { MONTHS_SHORT } from "@/lib/format";
import { MONEY_METRICS, parseMoneyMetricColumnKey, type MoneyMetric } from "@/lib/money-column-metrics";
import { accrualTypeLabel } from "@/lib/finance-accrual-type-labels";
import { DAILY_HISTORY_DAYS } from "@/lib/daily-history-depth";

export const PERIOD_COLUMN_PREFIX = "period:";
/**
 * Метка в extraCategories редактора колонок: «в этом блоке есть колонки за
 * период». Редактор не показывает отдельный раздел, а раскладывает их по
 * своим категориям рядом с колонками 7/14/30 дней (FlowColumnsEditor.buildCats).
 */
export const PERIOD_COLUMNS_CATEGORY_MARKER = "__period_columns__";

export const PERIOD_SOURCES = ["orders", "money", "analytics", "ads", "stock", "returns", "supplies", "price"] as const;
export type PeriodSource = (typeof PERIOD_SOURCES)[number];

export const PERIOD_SOURCE_LABELS: Record<PeriodSource, string> = {
  orders: "Заказы",
  money: "Деньги",
  analytics: "Аналитика",
  ads: "Реклама",
  stock: "Остатки",
  returns: "Возвраты",
  supplies: "Заявки на поставку",
  price: "Цена",
};

/** Покрытие/итог без данных по всем источникам — одна заготовка на сервер и клиент. */
export function emptyPeriodCoverage(days = 0): Record<PeriodSource, PeriodCoverage> {
  const empty = (): PeriodCoverage => ({ days, loaded: 0, from: null, to: null });
  return { orders: empty(), money: empty(), analytics: empty(), ads: empty(), stock: empty(), returns: empty(), supplies: empty(), price: empty() };
}

export type PeriodOrders = {
  /** Заказано штук по дню заказа, с отменами (как в кабинете). */
  ordered: number;
  /** Отгружено штук (начало обработки), без отмен. */
  shipped: number;
  /** Выплата Ozon по заказам (после комиссии и логистики) по дню заказа. */
  payout: number;
  /** Сумма заказов по цене продавца в кабинете. */
  cabinetSum: number;
  /** Сумма и число цен покупателя (есть не у всех дней). */
  customerSum: number;
  customerCount: number;
  /** Доля товара в заказах всех товаров выборки за отрезок, % (сумма по товарам — 100). */
  share: number | null;
  /** Дней отрезка с данными по заказам — знаменатель «дней без заказов». */
  days: number;
  /** Дни отрезка, когда были заказы: маска битов от первого дня (encodeDayMask). */
  activeDays: string;
};

export type PeriodAnalytics = {
  ordered_units: number;
  revenue: number;
  returns: number;
  cancellations: number;
  delivered_units: number;
  hits_view: number;
  hits_view_search: number;
  hits_view_pdp: number;
  hits_tocart: number;
  hits_tocart_search: number;
  hits_tocart_pdp: number;
  session_view: number;
};

export type PeriodAds = {
  spend: number;
  views: number;
  clicks: number;
  toCart: number;
  orders: number;
  revenue: number;
  modelOrders: number;
  modelRevenue: number;
};

export type PeriodStock = {
  /** Остаток (FBO + FBS) на последний день отрезка, за который есть снимок. */
  end: number | null;
  /** Средний остаток по дням отрезка со снимками. */
  avg: number | null;
  /** Дней отрезка, когда остатка не было (снимок есть и он нулевой). */
  zeroDays: number;
  /** Дней отрезка со снимками — знаменатель среднего и доли дней в нуле. */
  days: number;
  /** В пути на склады Ozon на последний день отрезка. */
  inTransitEnd: number | null;
};

export type PeriodReturns = {
  /** Возвраты покупателей после получения (штук). */
  returns: number;
  /** Невыкупы и отказы до получения (штук). */
  cancellations: number;
};

export type PeriodSupplies = {
  /** Штук в заявках на поставку, созданных в отрезке. */
  requestedQty: number;
  /** Заявок на поставку, созданных в отрезке. */
  requests: number;
};

export type PeriodPrice = {
  /** Средняя цена в кабинете по дням отрезка, где цена известна. */
  avg: number | null;
  /** Цена на последний день отрезка. */
  end: number | null;
  /** Сколько раз цена менялась внутри отрезка. */
  changes: number;
  /** Дней отрезка, где цена известна. */
  days: number;
};

export type PeriodItem = {
  orders: PeriodOrders | null;
  money: ProductMoney | null;
  analytics: PeriodAnalytics | null;
  ads: PeriodAds | null;
  stock?: PeriodStock | null;
  returns?: PeriodReturns | null;
  supplies?: PeriodSupplies | null;
  price?: PeriodPrice | null;
};

/** За сколько дней отрезка у источника есть данные и какие это дни. */
export type PeriodCoverage = {
  /** Дней в отрезке. */
  days: number;
  /** Дней с данными. */
  loaded: number;
  /** Первый и последний день с данными (null — данных нет). */
  from: string | null;
  to: string | null;
};

/** Что Ozon даёт магазину по аналитике (lib/analytics-quota.ts) — для подсказок. */
export type PeriodAnalyticsAccess = {
  /** Полный доступ (Premium Plus / Pro): все показатели, история без ограничения. */
  premium: boolean;
  /** Без подписки: с какой даты Ozon отдаёт аналитику (3 месяца назад); null — без ограничения. */
  availableFrom: string | null;
  /** Без подписки: запросов в сутки; null — без ограничения. */
  dailyLimit: number | null;
};

export type PeriodMetricsPayload = {
  from: string;
  to: string;
  days: number;
  /** Ключ — артикул или артикул с префиксом магазина (как у финансов таблицы). */
  items: Record<string, PeriodItem>;
  coverage: Record<PeriodSource, PeriodCoverage>;
  /** Виды услуг Ozon за отрезок — для раскрытия групп денег. */
  types: MoneyTypeInfo[];
  /** Доступ к аналитике Ozon (по худшему из магазинов). */
  analytics: PeriodAnalyticsAccess;
};

export type PeriodColumnKind = "money" | "count" | "pct" | "number";

/**
 * Итог по модели/таблице: сумма, отношение сумм, среднее по строкам со
 * значением, наибольшее (дней в нуле у модели — по самому долгому размеру)
 * или свой расчёт по товарам.
 */
export type PeriodAggregate =
  | { kind: "sum" }
  | { kind: "avg" }
  | { kind: "max" }
  | { kind: "ratio"; numerator: (item: PeriodItem) => number | null; denominator: (item: PeriodItem) => number | null; scale: number }
  /** Итог не выводится из чисел строк (дни без заказов у модели — объединение дней размеров): считает сам по товарам. */
  | { kind: "custom"; total: (items: readonly PeriodItem[]) => number | null };

export type PeriodColumnDef = {
  key: string;
  source: PeriodSource;
  metric: string;
  label: string;
  /** {P} — «за выбранный период». */
  description: string;
  kind: PeriodColumnKind;
  width: number;
  /** Подгруппа в редакторе и полоса группы в шапке (без дат — их дописывает таблица). */
  group: string;
  /** Ключ колонки с фиксированным периодом, рядом с которой стоять в редакторе. */
  counterpart?: string;
  /** Полное имя для редактора, если «<label> за период» читается плохо. */
  fullLabel?: string;
  /** Другие источники, которые нужны значению (доля возвратов — заказы). */
  needs?: readonly PeriodSource[];
  get: (item: PeriodItem) => number | null;
  aggregate: PeriodAggregate;
};

const sum: PeriodAggregate = { kind: "sum" };
const avg: PeriodAggregate = { kind: "avg" };
const max: PeriodAggregate = { kind: "max" };
const ratio = (numerator: (item: PeriodItem) => number | null, denominator: (item: PeriodItem) => number | null, scale = 1): PeriodAggregate =>
  ({ kind: "ratio", numerator, denominator, scale });
const pct = (a: number, b: number): number | null => (b > 0 ? Math.round(a / b * 1000) / 10 : null);
const per = (a: number, b: number): number | null => (b > 0 ? Math.round(a / b * 100) / 100 : null);

export function periodColumnKey(source: PeriodSource, metric: string): string {
  return `${PERIOD_COLUMN_PREFIX}${source}:${metric}`;
}

/**
 * Дни отрезка с событием — маской битов: бит i = день i от начала отрезка,
 * hex по байту (год — 92 знака). Нужна, чтобы у модели считать дни без
 * заказов точно: день модели «с заказом», если заказ был хоть у одного
 * размера, — из чисел размеров это не сложить.
 */
export function encodeDayMask(dayIndexes: Iterable<number>, days: number): string {
  const bytes = new Uint8Array(Math.max(0, Math.ceil(days / 8)));
  for (const index of dayIndexes) {
    if (!Number.isInteger(index) || index < 0 || index >= days) continue;
    bytes[index >> 3] |= 1 << (index & 7);
  }
  return Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
}

/** Сколько дней есть хотя бы в одной маске (объединение дней с событием). */
export function countDayMaskUnion(masks: readonly string[]): number {
  const length = Math.max(0, ...masks.map(mask => mask.length));
  let count = 0;
  for (let at = 0; at < length; at += 1) {
    let nibble = 0;
    for (const mask of masks) nibble |= parseInt(mask[at] ?? "0", 16) || 0;
    while (nibble) { count += nibble & 1; nibble >>= 1; }
  }
  return count;
}

/** Дней без заказов у набора товаров: дни с данными минус дни, когда заказ был хоть у одного. */
function ordersZeroDays(orders: ReadonlyArray<PeriodOrders | null | undefined>): number | null {
  const known = orders.filter((value): value is PeriodOrders => !!value);
  if (!known.length) return null;
  return Math.max(...known.map(value => value.days)) - countDayMaskUnion(known.map(value => value.activeDays));
}

const ORDER_COLUMNS: PeriodColumnDef[] = [
  { key: periodColumnKey("orders", "ordered"), source: "orders", metric: "ordered", label: "Заказы", kind: "count", width: 92, group: "Заказы", counterpart: "orders_30d", aggregate: sum,
    description: "Штук заказано {P} по дню заказа, вместе с отменёнными — как «заказано» в кабинете Ozon.", get: item => item.orders?.ordered ?? null },
  { key: periodColumnKey("orders", "shipped"), source: "orders", metric: "shipped", label: "Отгружено", kind: "count", width: 96, group: "Заказы", aggregate: sum,
    description: "Штук, по которым началась обработка {P}, без отменённых.", get: item => item.orders?.shipped ?? null },
  { key: periodColumnKey("orders", "cabinet"), source: "orders", metric: "cabinet", label: "Заказы по цене кабинета", kind: "money", width: 116, group: "Заказы", aggregate: sum,
    description: "Сумма заказов {P} по цене продавца в кабинете (цена × штуки, с отменёнными).", get: item => item.orders?.cabinetSum ?? null },
  { key: periodColumnKey("orders", "payout"), source: "orders", metric: "payout", label: "К выплате за заказы", kind: "money", width: 120, group: "Заказы", aggregate: sum,
    description: "Выплата Ozon по заказам {P} (после комиссии и логистики), по дню заказа.", get: item => item.orders?.payout ?? null },
  { key: periodColumnKey("orders", "avg_price"), source: "orders", metric: "avg_price", label: "Средняя цена заказа", kind: "money", width: 100, group: "Заказы",
    aggregate: ratio(item => item.orders?.cabinetSum ?? null, item => item.orders?.ordered ?? null),
    description: "Средняя цена заказанной штуки {P}: сумма по цене в кабинете ÷ заказано штук.",
    get: item => item.orders ? per(item.orders.cabinetSum, item.orders.ordered) : null },
  { key: periodColumnKey("orders", "per_day"), source: "orders", metric: "per_day", label: "Заказов в день", kind: "number", width: 96, group: "Заказы", counterpart: "orders_speed_30d", aggregate: sum,
    description: "Заказано штук {P} ÷ число дней периода.", get: item => item.orders?.ordered != null ? item.orders.ordered : null },
  { key: periodColumnKey("orders", "customer_price"), source: "orders", metric: "customer_price", label: "Оплачено ср.", fullLabel: "Оплачено ср. за период", kind: "money", width: 100, group: "Заказы", counterpart: "customer_price_avg_7d",
    aggregate: ratio(item => item.orders?.customerSum ?? null, item => item.orders?.customerCount ?? null),
    description: "Средняя сумма, оплаченная покупателем за штуку {P}: сумма цен покупателя ÷ штук с известной ценой (как «Оплачено ср. 7д», но за отрезок календаря).",
    get: item => item.orders ? per(item.orders.customerSum, item.orders.customerCount) : null },
  { key: periodColumnKey("orders", "share"), source: "orders", metric: "share", label: "Доля в заказах", kind: "pct", width: 100, group: "Заказы", counterpart: "orders_share", aggregate: sum,
    description: "Доля товара в заказах {P}: заказано штук ÷ заказано штук по всем товарам магазина × 100 %. У модели — сумма долей размеров.",
    get: item => item.orders?.share ?? null },
  { key: periodColumnKey("orders", "zero_days"), source: "orders", metric: "zero_days", label: "Дней без заказов", kind: "count", width: 100, group: "Заказы", counterpart: "days_no_sales",
    aggregate: { kind: "custom", total: items => ordersZeroDays(items.map(item => item.orders)) },
    description: "Сколько дней {P} не было ни одного заказа — среди дней, за которые есть история заказов. У модели день считается с заказом, если заказ был хоть у одного размера.",
    get: item => ordersZeroDays([item.orders]) },
];

const ANALYTICS_COLUMNS: PeriodColumnDef[] = [
  { key: periodColumnKey("analytics", "hits_view"), source: "analytics", metric: "hits_view", label: "Показы", kind: "count", width: 92, group: "Аналитика", counterpart: "hits_view", aggregate: sum,
    description: "Показы товара {P} в поиске, каталоге и на карточке (аналитика Ozon).", get: item => item.analytics?.hits_view ?? null },
  { key: periodColumnKey("analytics", "hits_view_search"), source: "analytics", metric: "hits_view_search", label: "Показы в поиске", kind: "count", width: 104, group: "Аналитика", counterpart: "hits_search", aggregate: sum,
    description: "Показы в поиске и каталоге {P}.", get: item => item.analytics?.hits_view_search ?? null },
  { key: periodColumnKey("analytics", "hits_view_pdp"), source: "analytics", metric: "hits_view_pdp", label: "Показы карточки", kind: "count", width: 104, group: "Аналитика", counterpart: "hits_pdp", aggregate: sum,
    description: "Показы карточки товара {P}.", get: item => item.analytics?.hits_view_pdp ?? null },
  { key: periodColumnKey("analytics", "hits_tocart"), source: "analytics", metric: "hits_tocart", label: "В корзину", kind: "count", width: 92, group: "Аналитика", counterpart: "hits_tocart", aggregate: sum,
    description: "Добавлений в корзину {P}.", get: item => item.analytics?.hits_tocart ?? null },
  { key: periodColumnKey("analytics", "hits_tocart_search"), source: "analytics", metric: "hits_tocart_search", label: "В корзину из поиска", kind: "count", width: 120, group: "Аналитика", counterpart: "hits_tocart_search", aggregate: sum,
    description: "Добавлений в корзину из поиска и категории {P}.", get: item => item.analytics?.hits_tocart_search ?? null },
  { key: periodColumnKey("analytics", "hits_tocart_pdp"), source: "analytics", metric: "hits_tocart_pdp", label: "В корзину с карточки", kind: "count", width: 124, group: "Аналитика", counterpart: "hits_tocart_pdp", aggregate: sum,
    description: "Добавлений в корзину с карточки товара {P}.", get: item => item.analytics?.hits_tocart_pdp ?? null },
  { key: periodColumnKey("analytics", "conv_tocart"), source: "analytics", metric: "conv_tocart", label: "Конверсия в корзину", kind: "pct", width: 108, group: "Аналитика", counterpart: "conv_tocart",
    aggregate: ratio(item => item.analytics?.hits_tocart ?? null, item => item.analytics?.hits_view ?? null, 100),
    description: "В корзину ÷ показы {P}, в процентах.",
    get: item => item.analytics ? pct(item.analytics.hits_tocart, item.analytics.hits_view) : null },
  { key: periodColumnKey("analytics", "session_view"), source: "analytics", metric: "session_view", label: "Сессии", kind: "count", width: 88, group: "Аналитика", counterpart: "session_view", aggregate: sum,
    description: "Уникальные посетители {P} (сессии с показом).", get: item => item.analytics?.session_view ?? null },
  { key: periodColumnKey("analytics", "ordered_units"), source: "analytics", metric: "ordered_units", label: "Заказано (аналитика)", kind: "count", width: 108, group: "Аналитика", counterpart: "ordered_units", aggregate: sum,
    description: "Заказано штук {P} по аналитике Ozon — для сверки с заказами.", get: item => item.analytics?.ordered_units ?? null },
  { key: periodColumnKey("analytics", "revenue"), source: "analytics", metric: "revenue", label: "Выручка (аналитика)", kind: "money", width: 120, group: "Аналитика", counterpart: "revenue", aggregate: sum,
    description: "Выручка {P} по аналитике Ozon (оплаченные заказы) — как «Выручка 30д», но за отрезок календаря.", get: item => item.analytics?.revenue ?? null },
  { key: periodColumnKey("analytics", "conv_order"), source: "analytics", metric: "conv_order", label: "Конверсия в заказ", kind: "pct", width: 104, group: "Аналитика",
    aggregate: ratio(item => item.analytics?.ordered_units ?? null, item => item.analytics?.hits_view ?? null, 100),
    description: "Заказано ÷ показы {P}, в процентах (по аналитике Ozon).",
    get: item => item.analytics ? pct(item.analytics.ordered_units, item.analytics.hits_view) : null },
  { key: periodColumnKey("analytics", "returns"), source: "analytics", metric: "returns", label: "Возвраты (аналитика)", kind: "count", width: 108, group: "Аналитика", counterpart: "returns", aggregate: sum,
    description: "Возвращено штук {P} по аналитике Ozon.", get: item => item.analytics?.returns ?? null },
  { key: periodColumnKey("analytics", "cancellations"), source: "analytics", metric: "cancellations", label: "Отмены", kind: "count", width: 88, group: "Аналитика", counterpart: "cancellations", aggregate: sum,
    description: "Отменено штук {P} по аналитике Ozon.", get: item => item.analytics?.cancellations ?? null },
  { key: periodColumnKey("analytics", "delivered_units"), source: "analytics", metric: "delivered_units", label: "Доставлено", kind: "count", width: 96, group: "Аналитика", counterpart: "delivered", aggregate: sum,
    description: "Доставлено штук {P} по аналитике Ozon.", get: item => item.analytics?.delivered_units ?? null },
  { key: periodColumnKey("analytics", "return_rate"), source: "analytics", metric: "return_rate", label: "% возвратов", fullLabel: "% возвратов за период", kind: "pct", width: 100, group: "Аналитика", counterpart: "return_rate",
    aggregate: ratio(item => item.analytics?.returns ?? null, item => item.analytics?.delivered_units ?? null, 100),
    description: "Возвраты ÷ доставлено {P} по аналитике Ozon, в процентах. Чем ниже — тем лучше.",
    get: item => item.analytics ? pct(item.analytics.returns, item.analytics.delivered_units) : null },
];

const ADS_COLUMNS: PeriodColumnDef[] = [
  { key: periodColumnKey("ads", "spend"), source: "ads", metric: "spend", label: "Расход на рекламу", kind: "money", width: 110, group: "Реклама", aggregate: sum,
    description: "Расход на рекламу товара {P} по статистике Performance.", get: item => item.ads?.spend ?? null },
  { key: periodColumnKey("ads", "views"), source: "ads", metric: "views", label: "Показы рекламы", kind: "count", width: 100, group: "Реклама", aggregate: sum,
    description: "Рекламные показы {P}.", get: item => item.ads?.views ?? null },
  { key: periodColumnKey("ads", "clicks"), source: "ads", metric: "clicks", label: "Клики", kind: "count", width: 84, group: "Реклама", aggregate: sum,
    description: "Клики по рекламе {P}.", get: item => item.ads?.clicks ?? null },
  { key: periodColumnKey("ads", "ctr"), source: "ads", metric: "ctr", label: "CTR", kind: "pct", width: 76, group: "Реклама",
    aggregate: ratio(item => item.ads?.clicks ?? null, item => item.ads?.views ?? null, 100),
    description: "Клики ÷ рекламные показы {P}, в процентах.", get: item => item.ads ? pct(item.ads.clicks, item.ads.views) : null },
  { key: periodColumnKey("ads", "cpc"), source: "ads", metric: "cpc", label: "Цена клика", kind: "money", width: 92, group: "Реклама",
    aggregate: ratio(item => item.ads?.spend ?? null, item => item.ads?.clicks ?? null),
    description: "Расход ÷ клики {P}.", get: item => item.ads ? per(item.ads.spend, item.ads.clicks) : null },
  { key: periodColumnKey("ads", "to_cart"), source: "ads", metric: "to_cart", label: "В корзину с рекламы", kind: "count", width: 124, group: "Реклама", aggregate: sum,
    description: "Добавлений в корзину через рекламу {P}.", get: item => item.ads?.toCart ?? null },
  { key: periodColumnKey("ads", "orders"), source: "ads", metric: "orders", label: "Заказы с рекламы", kind: "count", width: 104, group: "Реклама", aggregate: sum,
    description: "Прямые заказы с рекламы {P} (штук).", get: item => item.ads?.orders ?? null },
  { key: periodColumnKey("ads", "model_orders"), source: "ads", metric: "model_orders", label: "Заказы модели", kind: "count", width: 100, group: "Реклама", aggregate: sum,
    description: "Заказы модели с рекламы {P}: покупатель кликнул рекламу одного варианта товара, а купил другой (размер, цвет).", get: item => item.ads?.modelOrders ?? null },
  { key: periodColumnKey("ads", "total_orders"), source: "ads", metric: "total_orders", label: "Всего заказов с рекламы", kind: "count", width: 130, group: "Реклама", aggregate: sum,
    description: "Все рекламные заказы {P}: прямые + модели.", get: item => item.ads ? item.ads.orders + item.ads.modelOrders : null },
  { key: periodColumnKey("ads", "revenue"), source: "ads", metric: "revenue", label: "Выручка с рекламы", kind: "money", width: 110, group: "Реклама", aggregate: sum,
    description: "Выручка от прямых заказов с рекламы {P}.", get: item => item.ads?.revenue ?? null },
  { key: periodColumnKey("ads", "model_revenue"), source: "ads", metric: "model_revenue", label: "Выручка модели", kind: "money", width: 110, group: "Реклама", aggregate: sum,
    description: "Выручка от заказов модели с рекламы {P}.", get: item => item.ads?.modelRevenue ?? null },
  { key: periodColumnKey("ads", "total_revenue"), source: "ads", metric: "total_revenue", label: "Всего выручки с рекламы", kind: "money", width: 136, group: "Реклама", aggregate: sum,
    description: "Вся рекламная выручка {P}: прямая + модели.", get: item => item.ads ? Math.round((item.ads.revenue + item.ads.modelRevenue) * 100) / 100 : null },
  { key: periodColumnKey("ads", "conv_cart"), source: "ads", metric: "conv_cart", label: "Конверсия в корзину", kind: "pct", width: 108, group: "Реклама",
    aggregate: ratio(item => item.ads?.toCart ?? null, item => item.ads?.clicks ?? null, 100),
    description: "В корзину с рекламы ÷ клики {P}, в процентах.", get: item => item.ads ? pct(item.ads.toCart, item.ads.clicks) : null },
  { key: periodColumnKey("ads", "conv_order"), source: "ads", metric: "conv_order", label: "Конверсия в заказ", kind: "pct", width: 104, group: "Реклама",
    aggregate: ratio(item => (item.ads ? item.ads.orders + item.ads.modelOrders : null), item => item.ads?.clicks ?? null, 100),
    description: "Все рекламные заказы (прямые + модели) ÷ клики {P}, в процентах.", get: item => item.ads ? pct(item.ads.orders + item.ads.modelOrders, item.ads.clicks) : null },
  { key: periodColumnKey("ads", "drr"), source: "ads", metric: "drr", label: "ДРР", kind: "pct", width: 76, group: "Реклама",
    aggregate: ratio(item => item.ads?.spend ?? null, item => item.ads?.revenue ?? null, 100),
    description: "Расход ÷ выручка с рекламы {P}, в процентах.", get: item => item.ads ? pct(item.ads.spend, item.ads.revenue) : null },
  { key: periodColumnKey("ads", "total_drr"), source: "ads", metric: "total_drr", label: "ДРР с мод.", fullLabel: "ДРР с моделями за период", kind: "pct", width: 100, group: "Реклама",
    aggregate: ratio(item => item.ads?.spend ?? null, item => (item.ads ? item.ads.revenue + item.ads.modelRevenue : null), 100),
    description: "Расход ÷ (прямая + модельная выручка) {P}, в процентах.", get: item => item.ads ? pct(item.ads.spend, item.ads.revenue + item.ads.modelRevenue) : null },
  { key: periodColumnKey("ads", "cost_per_order"), source: "ads", metric: "cost_per_order", label: "Цена заказа", kind: "money", width: 96, group: "Реклама",
    aggregate: ratio(item => item.ads?.spend ?? null, item => item.ads?.orders ?? null),
    description: "Расход ÷ заказы с рекламы {P}.", get: item => item.ads ? per(item.ads.spend, item.ads.orders) : null },
];

function moneyColumnDef(metric: MoneyMetric): PeriodColumnDef {
  const agg = metric.aggregate;
  const aggregate: PeriodAggregate = agg.kind === "sum"
    ? sum
    : ratio(item => (item.money ? agg.numerator(item.money) : null), item => (item.money ? agg.denominator(item.money) : null), agg.scale);
  return {
    key: periodColumnKey("money", metric.key),
    source: "money",
    metric: metric.key,
    label: metric.label,
    // {P} подставляется подписью периода так же, как у колонок 7/14/30 дней.
    description: metric.description,
    kind: metric.kind,
    width: metric.width,
    group: `Деньги · ${metric.group}`,
    get: item => (item.money ? metric.get(item.money) : null),
    aggregate,
  };
}

/** Пары «за 30 дней по начислениям» → «за период»: выкупы и возвращённое стоят рядом с ними в «Аналитике». */
const MONEY_COUNTERPARTS: Record<string, string> = { buyouts: "fin_buyout_count", return_units: "fin_return_count" };
const MONEY_COLUMNS: PeriodColumnDef[] = MONEY_METRICS.map(metric => {
  const def = moneyColumnDef(metric);
  const counterpart = MONEY_COUNTERPARTS[metric.key];
  return counterpart ? { ...def, counterpart } : def;
});
const MONEY_METRIC_BY_KEY = new Map(MONEY_METRICS.map(metric => [metric.key, metric]));

const STOCK_COLUMNS: PeriodColumnDef[] = [
  { key: periodColumnKey("stock", "end"), source: "stock", metric: "end", label: "Остаток на конец", fullLabel: "Остаток на конец периода", kind: "count", width: 104, group: "Остатки", counterpart: "stock", aggregate: sum,
    description: "Остаток на складах Ozon (FBO + FBS) на последний день {P}, за который есть утренний снимок.", get: item => item.stock?.end ?? null },
  { key: periodColumnKey("stock", "avg"), source: "stock", metric: "avg", label: "Средний остаток", kind: "number", width: 116, group: "Остатки", aggregate: sum,
    description: "Средний остаток на складах Ozon по дням {P}: сумма снимков ÷ дней со снимком.", get: item => item.stock?.avg ?? null },
  { key: periodColumnKey("stock", "zero_days"), source: "stock", metric: "zero_days", label: "Дней в нуле", kind: "count", width: 92, group: "Остатки", counterpart: "days_no_sales", aggregate: max,
    description: "Сколько дней {P} остатка на складах Ozon не было вовсе (утренний снимок — ноль). У модели — наибольшее среди размеров.", get: item => item.stock?.zeroDays ?? null },
  { key: periodColumnKey("stock", "in_transit_end"), source: "stock", metric: "in_transit_end", label: "В пути на конец", fullLabel: "В пути на конец периода", kind: "count", width: 100, group: "Остатки", counterpart: "stock_in_transit", aggregate: sum,
    description: "Штук в пути на склады Ozon на последний день {P}, за который есть снимок.", get: item => item.stock?.inTransitEnd ?? null },
];

const RETURN_COLUMNS: PeriodColumnDef[] = [
  { key: periodColumnKey("returns", "returns"), source: "returns", metric: "returns", label: "Возвраты покупателей", kind: "count", width: 108, group: "Возвраты", aggregate: sum,
    description: "Штук, которые покупатели вернули после получения, по дню начала возврата {P} (список возвратов Ozon).", get: item => item.returns?.returns ?? null },
  { key: periodColumnKey("returns", "cancellations"), source: "returns", metric: "cancellations", label: "Невыкупы", kind: "count", width: 92, group: "Возвраты", aggregate: sum,
    description: "Штук, от которых покупатели отказались до получения (невыкуп, отмена), по дню начала возврата {P}.", get: item => item.returns?.cancellations ?? null },
  { key: periodColumnKey("returns", "rate"), source: "returns", metric: "rate", label: "Доля возвратов", kind: "pct", width: 100, group: "Возвраты", needs: ["orders"],
    aggregate: ratio(item => (item.returns ? item.returns.returns + item.returns.cancellations : null), item => item.orders?.ordered ?? null, 100),
    description: "Возвраты и невыкупы {P} ÷ заказано штук {P}, в процентах.",
    get: item => (item.returns && item.orders ? pct(item.returns.returns + item.returns.cancellations, item.orders.ordered) : null) },
];

const SUPPLY_COLUMNS: PeriodColumnDef[] = [
  { key: periodColumnKey("supplies", "requested"), source: "supplies", metric: "requested", label: "Заявлено на поставку", kind: "count", width: 112, group: "Поставки", counterpart: "supply_total_30d", aggregate: sum,
    description: "Штук в заявках на поставку, созданных {P} (по дню создания заявки в кабинете).", get: item => item.supplies?.requestedQty ?? null },
  { key: periodColumnKey("supplies", "requests"), source: "supplies", metric: "requests", label: "Заявок на поставку", kind: "count", width: 100, group: "Поставки", counterpart: "supply_count_30d", aggregate: sum,
    description: "Заявок на поставку с этим товаром, созданных {P}.", get: item => item.supplies?.requests ?? null },
];

const PRICE_COLUMNS: PeriodColumnDef[] = [
  { key: periodColumnKey("price", "avg"), source: "price", metric: "avg", label: "Средняя цена", kind: "money", width: 100, group: "Цены", counterpart: "price", aggregate: avg,
    description: "Средняя цена в кабинете по дням {P}: день без записи считается по последней известной цене. У модели — среднее по размерам.", get: item => item.price?.avg ?? null },
  { key: periodColumnKey("price", "end"), source: "price", metric: "end", label: "Цена на конец", fullLabel: "Цена на конец периода", kind: "money", width: 100, group: "Цены", aggregate: avg,
    description: "Цена в кабинете на последний день {P}. У модели — среднее по размерам.", get: item => item.price?.end ?? null },
  { key: periodColumnKey("price", "changes"), source: "price", metric: "changes", label: "Смен цены", kind: "count", width: 88, group: "Цены", aggregate: max,
    description: "Сколько раз цена в кабинете менялась {P}. У модели — наибольшее среди размеров.", get: item => item.price?.changes ?? null },
];

export const PERIOD_COLUMN_DEFS: readonly PeriodColumnDef[] = [
  ...ORDER_COLUMNS, ...MONEY_COLUMNS, ...ANALYTICS_COLUMNS, ...ADS_COLUMNS, ...STOCK_COLUMNS, ...RETURN_COLUMNS, ...SUPPLY_COLUMNS, ...PRICE_COLUMNS,
];

const DEF_BY_KEY = new Map(PERIOD_COLUMN_DEFS.map(def => [def.key, def]));

export function isPeriodColumnKey(key: string): boolean {
  return key.startsWith(PERIOD_COLUMN_PREFIX);
}

/**
 * Колонка по виду услуги Ozon за отрезок календаря: `period:money:type_<id>`.
 * Виды у каждого магазина свои, поэтому описание собирается по ключу, а не
 * лежит в списке; значение — сумма вида из денег товара (byType), как у
 * колонок money_type_<id>_<период>.
 */
export function moneyTypePeriodColumnKey(typeId: number): string {
  return periodColumnKey("money", `type_${typeId}`);
}

/** Разбор колонки денег за период: показатель (metric) или вид услуги (typeId). */
export function parseMoneyPeriodColumnKey(key: string): { metric: string; typeId: number | null } | null {
  if (!key.startsWith(`${PERIOD_COLUMN_PREFIX}money:`)) return null;
  const metric = key.slice(PERIOD_COLUMN_PREFIX.length + "money:".length);
  const type = /^type_(\d+)$/.exec(metric);
  return { metric, typeId: type ? Number(type[1]) : null };
}

const TYPE_DEFS = new Map<number, PeriodColumnDef>();

function moneyTypeColumnDef(typeId: number): PeriodColumnDef {
  let def = TYPE_DEFS.get(typeId);
  if (!def) {
    const label = accrualTypeLabel(typeId);
    def = {
      key: moneyTypePeriodColumnKey(typeId),
      source: "money",
      metric: `type_${typeId}`,
      label,
      description: `Услуга Ozon «${label}» (тип начисления ${typeId}) по этому товару.\nКак считается:\n• Ozon начислил её на товар — сумма напрямую\n• начислена без артикула (общая) — доля товара: складские по проданным штукам, остальные по выручке\nСчитается {P}.`,
      kind: "money",
      width: 110,
      group: "Деньги · виды услуг",
      get: item => item.money?.byType[String(typeId)] ?? null,
      aggregate: sum,
    };
    TYPE_DEFS.set(typeId, def);
  }
  return def;
}

/**
 * Полное имя для редактора и списков: «Заказы за период» — рядом с «Заказы
 * 30 дней». У денег колонок за фиксированные дни в списке больше нет, отрезок
 * задаёт календарь блока — подпись без хвоста: «Прибыль».
 */
export function periodColumnFullLabel(def: PeriodColumnDef): string {
  if (def.fullLabel) return def.fullLabel;
  return def.source === "money" ? def.label : `${def.label} за период`;
}

export function getPeriodColumnDef(key: string): PeriodColumnDef | null {
  const known = DEF_BY_KEY.get(key);
  if (known) return known;
  const parsed = parseMoneyPeriodColumnKey(key);
  return parsed?.typeId != null ? moneyTypeColumnDef(parsed.typeId) : null;
}

/**
 * Колонка-полоска «Структура выручки» — за период (period:money:structure)
 * или за 7/14/30 дней (money_structure_*): таблице нужна ширина в три
 * стандартных, иначе куски полоски не разглядеть.
 */
export function isRevenueStructureColumnKey(key: string): boolean {
  const metric = parseMoneyMetricColumnKey(key)?.metric ?? MONEY_METRIC_BY_KEY.get(parseMoneyPeriodColumnKey(key)?.metric ?? "");
  return metric?.structure === true;
}

/** Ключи колонок по источникам — для категории редактора. */
export function periodColumnKeysBySource(): Record<PeriodSource, string[]> {
  const out: Record<PeriodSource, string[]> = { orders: [], money: [], analytics: [], ads: [], stock: [], returns: [], supplies: [], price: [] };
  for (const def of PERIOD_COLUMN_DEFS) out[def.source].push(def.key);
  return out;
}

/**
 * Колонки «за период» по их паре с фиксированным окном: «Заказы 30 дней» →
 * «Заказы за период». Редактор ставит календарную колонку сразу после пары в
 * каждой категории, где та лежит.
 */
export function periodColumnKeysByCounterpart(): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const def of PERIOD_COLUMN_DEFS) {
    if (!def.counterpart) continue;
    const list = out.get(def.counterpart) ?? [];
    list.push(def.key);
    out.set(def.counterpart, list);
  }
  return out;
}

/** Источники, которые нужны набору колонок (что запрашивать у сервера). */
export function periodSourcesOfColumns(columns: readonly string[]): PeriodSource[] {
  const sources = new Set<PeriodSource>();
  for (const key of columns) {
    const def = getPeriodColumnDef(key);
    if (!def) continue;
    sources.add(def.source);
    for (const extra of def.needs ?? []) sources.add(extra);
  }
  return PERIOD_SOURCES.filter(source => sources.has(source));
}

export type PeriodRange = { from: string; to: string };

const DAY_MS = 86_400_000;
const MSK_OFFSET_MS = 3 * 60 * 60 * 1000;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Сегодня по Москве — та же граница дня, что у данных на сервере. */
export function mskTodayIso(now = Date.now()): string {
  return new Date(now + MSK_OFFSET_MS).toISOString().slice(0, 10);
}

export function shiftIsoDays(value: string, days: number): string {
  return new Date(Date.parse(`${value}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

/** Период блока из атрибута; без него или с битым значением — последние 30 дней до вчера. */
export function resolveBlockPeriod(raw: unknown, today = mskTodayIso()): PeriodRange {
  const floor = shiftIsoDays(today, -DAILY_HISTORY_DAYS);
  const candidate = raw && typeof raw === "object" ? raw as { from?: unknown; to?: unknown } : null;
  const from = typeof candidate?.from === "string" && ISO_DATE.test(candidate.from) ? candidate.from : null;
  const to = typeof candidate?.to === "string" && ISO_DATE.test(candidate.to) ? candidate.to : null;
  if (from && to && from <= to && to <= today && from >= floor) return { from, to };
  return defaultBlockPeriod(today);
}

/** Период по умолчанию: последние 30 дней до вчера — его же сервер греет после обновления. */
export function defaultBlockPeriod(today = mskTodayIso()): PeriodRange {
  return { from: shiftIsoDays(today, -30), to: shiftIsoDays(today, -1) };
}

/**
 * URL запроса показателей за период — один и тот же у таблицы (useFlowTablePeriod)
 * и у прогрева страницы (flowPageWarmupUrls): совпадение строки нужно, чтобы
 * ответ прогрева достался таблице.
 */
export function periodMetricsUrl(range: PeriodRange, sources: readonly PeriodSource[]): string {
  return `/api/period-metrics?from=${range.from}&to=${range.to}&sources=${sources.join(",")}`;
}

export function periodDaysCount(range: PeriodRange): number {
  return Math.max(0, Math.round((Date.parse(`${range.to}T00:00:00Z`) - Date.parse(`${range.from}T00:00:00Z`)) / DAY_MS) + 1);
}

/** «1–30 июн» / «25 мая – 3 июн» / «1 дек 2025 – 31 янв 2026» — для кнопки и полосы группы. */
export function formatPeriodRange(range: PeriodRange, opts: { year?: "auto" | "always" | "never" } = {}): string {
  const from = new Date(`${range.from}T00:00:00Z`);
  const to = new Date(`${range.to}T00:00:00Z`);
  const month = (d: Date) => MONTHS_SHORT[d.getUTCMonth()];
  const sameYear = from.getUTCFullYear() === to.getUTCFullYear();
  const currentYear = new Date().getUTCFullYear();
  const showYear = opts.year === "always" || (opts.year !== "never" && (!sameYear || from.getUTCFullYear() !== currentYear));
  const yearOf = (d: Date) => (showYear ? ` ${d.getUTCFullYear()}` : "");
  if (range.from === range.to) return `${from.getUTCDate()} ${month(from)}${yearOf(from)}`;
  if (sameYear && from.getUTCMonth() === to.getUTCMonth()) {
    return `${from.getUTCDate()}–${to.getUTCDate()} ${month(to)}${yearOf(to)}`;
  }
  return `${from.getUTCDate()} ${month(from)}${sameYear ? "" : yearOf(from)} – ${to.getUTCDate()} ${month(to)}${yearOf(to)}`;
}

/** Значение колонки для товара; «заказов в день» делится на длину периода здесь. */
export function periodColumnValue(def: PeriodColumnDef, item: PeriodItem | null | undefined, days: number): number | null {
  if (!item) return null;
  const raw = def.get(item);
  if (raw == null) return null;
  if (def.metric === "per_day" && def.source === "orders") return days > 0 ? Math.round(raw / days * 100) / 100 : null;
  return raw;
}

/**
 * Итог по нескольким товарам (модель, вся таблица): суммы складываются,
 * отношения считаются из сумм числителя и знаменателя — не среднее процентов.
 */
export function periodColumnTotal(def: PeriodColumnDef, items: ReadonlyArray<PeriodItem | null | undefined>, days: number): number | null {
  if (def.aggregate.kind === "custom") return def.aggregate.total(items.filter((item): item is PeriodItem => !!item));
  let seen = false;
  if (def.aggregate.kind === "sum" || def.aggregate.kind === "avg" || def.aggregate.kind === "max") {
    let total = 0;
    let count = 0;
    let best = -Infinity;
    for (const item of items) {
      const value = periodColumnValue(def, item, days);
      if (value == null) continue;
      seen = true;
      total += value;
      count += 1;
      if (value > best) best = value;
    }
    if (!seen) return null;
    if (def.aggregate.kind === "max") return best;
    if (def.aggregate.kind === "avg") return Math.round(total / count * 100) / 100;
    return Math.round(total * 100) / 100;
  }
  let numerator = 0;
  let denominator = 0;
  for (const item of items) {
    if (!item) continue;
    const a = def.aggregate.numerator(item);
    const b = def.aggregate.denominator(item);
    if (a == null || b == null) continue;
    seen = true;
    numerator += a;
    denominator += b;
  }
  if (!seen || !(Math.abs(denominator) > 0)) return null;
  return Math.round(numerator / denominator * def.aggregate.scale * 100) / 100;
}
